import { S3Client, PutObjectCommand, DeleteObjectsCommand, DeleteObjectCommand, ListObjectsV2Command, GetObjectCommand } from "@aws-sdk/client-s3";
import { DynamoDBClient, PutItemCommand, GetItemCommand, ScanCommand, DeleteItemCommand, UpdateItemCommand } from "@aws-sdk/client-dynamodb";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { marshall, unmarshall } from "@aws-sdk/util-dynamodb";

// AWS Configuration
const s3 = new S3Client({
    region: "us-east-1",
    credentials: {
        accessKeyId: "AKIA4SDNVV4XOPHOQ4TH",
        secretAccessKey: "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",
    },
});

const dynamoDB = new DynamoDBClient({
    region: "me-central-1",
    credentials: {
        accessKeyId: "AKIA4SDNVV4XOPHOQ4TH",
        secretAccessKey: "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",
    },
});

const ITEMS_TABLE_NAME = "alyaqeen";
const STATES_TABLE_NAME = "states";
const BUCKET_NAME = "alyaqeen";

// POST - Add item
export const createItem = async (item = {}) => {
    const params = {
        TableName: ITEMS_TABLE_NAME,
        Item: marshall(item),
    };

    try {
        await dynamoDB.send(new PutItemCommand(params));
        console.log("Item created successfully");
    } catch (error) {
        console.error("Error creating item:", error);
        throw error;
    }
};

// GET - retrieve item
export const getItem = async (id) => {
    const params = {
        TableName: ITEMS_TABLE_NAME,
        Key: marshall({ id }),
    };

    try {
        const { Item } = await dynamoDB.send(new GetItemCommand(params));
        return Item ? unmarshall(Item) : null;
    } catch (error) {
        console.error("Error getting item:", error);
        return null;
    }
};

// GET - retrieve all items
export const getAllItem = async () => {
    const params = {
        TableName: ITEMS_TABLE_NAME,
    };

    try {
        const { Items } = await dynamoDB.send(new ScanCommand(params));
        return Items ? Items.map(item => unmarshall(item)) : [];
    } catch (error) {
        console.error("Error fetching items:", error);
        return [];
    }
};

// DELETE - delete item
export const deleteItem = async (id) => {
    const params = {
        TableName: ITEMS_TABLE_NAME,
        Key: marshall({ id }),
    };

    try {
        await dynamoDB.send(new DeleteItemCommand(params));
        console.log("Item deleted successfully");
    } catch (error) {
        console.error("Error deleting item:", error);
        throw error;
    }
};

// DELETE - delete all items
export const deleteAllItems = async () => {
    try {
        // First get all items
        const items = await getAllItem();
        
        // Delete each item one by one
        for (const item of items) {
            await deleteItem(item.id);
        }
        
        console.log("All items deleted successfully");
    } catch (error) {
        console.error("Error deleting all items:", error);
        throw error;
    }
};

// PUT - update item
export const updateItem = async (id = "", updatedData = {}) => {
    // Remove id from updatedData to prevent changing the primary key
    delete updatedData.id;
    
    const updateExpression = [];
    const expressionAttributeValues = {};
    const expressionAttributeNames = {};
    
    // Dynamically build the update expression
    Object.keys(updatedData).forEach((key, index) => {
        const attributeKey = `:val${index}`;
        const attributeName = `#key${index}`;
        updateExpression.push(`${attributeName} = ${attributeKey}`);
        expressionAttributeValues[attributeKey] = updatedData[key];
        expressionAttributeNames[attributeName] = key;
    });
    
    const params = {
        TableName: ITEMS_TABLE_NAME,
        Key: marshall({ id }),
        UpdateExpression: `SET ${updateExpression.join(", ")}`,
        ExpressionAttributeValues: marshall(expressionAttributeValues),
        ExpressionAttributeNames: expressionAttributeNames,
        ReturnValues: "ALL_NEW",
    };

    try {
        const { Attributes } = await dynamoDB.send(new UpdateItemCommand(params));
        return unmarshall(Attributes);
    } catch (error) {
        console.error("Error updating item:", error);
        throw error;
    }
};

// POST - upload image to S3
export const uploadToS3 = async (file, product) => {
    if (!file) return console.error("No file selected");
    
    const REGION = "us-east-1";
    const fileName = `${product}/${file.name}`;
    const uploadParams = {
        Bucket: BUCKET_NAME,
        Key: fileName,
        ContentType: file.type,
    };

    try {
        // Generate a pre-signed URL for direct upload
        const signedUrl = await getSignedUrl(s3, new PutObjectCommand(uploadParams), { expiresIn: 60 });

        // Upload file directly to S3
        const response = await fetch(signedUrl, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": file.type },
        });

        if (response.ok) {
            console.log("✅ Upload successful:", `https://${BUCKET_NAME}.s3.${REGION}.amazonaws.com/${fileName}`);
            return `https://${BUCKET_NAME}.s3.${REGION}.amazonaws.com/${fileName}`;
        } else {
            throw new Error("Upload failed");
        }
    } catch (error) {
        console.error("❌ Upload error:", error);
        throw error;
    }
};

// GET - Images from s3
export const getSignedImageUrl = async (filePath) => {
    try {
        const command = new GetObjectCommand({
            Bucket: BUCKET_NAME,
            Key: filePath,
        });

        const signedUrl = await getSignedUrl(s3, command, { expiresIn: 3600 });
        return signedUrl;
    } catch (error) {
        console.error("Error generating signed URL:", error);
        return null;
    }
};

export const getS3FolderImages = async (folderPath) => {
    try {
        // Step 1: List all objects in the folder
        const listCommand = new ListObjectsV2Command({
            Bucket: BUCKET_NAME,
            Prefix: folderPath,
        });

        const listResponse = await s3.send(listCommand);
        if (!listResponse.Contents || listResponse.Contents.length === 0) {
            console.log("🚀 No images found in folder:", folderPath);
            return [];
        }

        // Step 2: Generate signed URLs for each image
        const imageUrls = await Promise.all(
            listResponse.Contents.map(async (obj) => {
                const getCommand = new GetObjectCommand({
                    Bucket: BUCKET_NAME,
                    Key: obj.Key,
                });
                return getSignedUrl(s3, getCommand, { expiresIn: 3600 });
            })
        );

        console.log("✅ Images retrieved:", imageUrls);
        return imageUrls;
    } catch (error) {
        console.error("❌ Error retrieving images:", error);
        return [];
    }
};

// DELETE - a specific item
export const deleteS3Image = async (filePath) => {
    try {
        const deleteCommand = new DeleteObjectCommand({
            Bucket: BUCKET_NAME,
            Key: filePath,
        });

        await s3.send(deleteCommand);
        console.log(`✅ Image deleted successfully: ${filePath}`);
    } catch (error) {
        console.error("❌ Error deleting image:", error);
        throw error;
    }
};

// DELETE - REMOVE AN ENTIRE FOLDER
export const deleteS3Folder = async (folderPath) => {
    try {
        // Step 1: List all objects inside the folder
        const listCommand = new ListObjectsV2Command({
            Bucket: BUCKET_NAME,
            Prefix: folderPath,
        });

        const listResponse = await s3.send(listCommand);
        if (!listResponse.Contents || listResponse.Contents.length === 0) {
            console.log("🚀 Folder is already empty or does not exist:", folderPath);
            return;
        }

        // Step 2: Delete all objects inside the folder
        const deleteCommand = new DeleteObjectsCommand({
            Bucket: BUCKET_NAME,
            Delete: {
                Objects: listResponse.Contents.map((obj) => ({ Key: obj.Key })),
            },
        });

        await s3.send(deleteCommand);
        console.log("✅ Folder deleted successfully:", folderPath);
    } catch (error) {
        console.error("❌ Error deleting folder:", error);
        throw error;
    }
};

// DELETE - all bucket content
export const deleteAllBucketContents = async () => {
    try {
        let objects;
        do {
            // Step 1: List all objects
            const listCommand = new ListObjectsV2Command({ Bucket: BUCKET_NAME });
            const listResponse = await s3.send(listCommand);

            objects = listResponse.Contents;
            if (!objects || objects.length === 0) {
                console.log("🚀 Bucket is already empty!");
                return;
            }

            // Step 2: Delete all objects in a batch
            const deleteCommand = new DeleteObjectsCommand({
                Bucket: BUCKET_NAME,
                Delete: {
                    Objects: objects.map((obj) => ({ Key: obj.Key })),
                },
            });

            await s3.send(deleteCommand);
            console.log(`✅ Deleted ${objects.length} objects from the bucket.`);
        } while (objects.length > 0);

        console.log("✅ All contents in the bucket are deleted!");
    } catch (error) {
        console.error("❌ Error deleting bucket contents:", error);
        throw error;
    }
};

/*=============states api==============*/
export const fetchStates = async () => {
    const params = {
        TableName: STATES_TABLE_NAME,
        Key: marshall({ id: "states" }),
    };

    try {
        const { Item } = await dynamoDB.send(new GetItemCommand(params));
        return Item ? unmarshall(Item).states : [];
    } catch (error) {
        console.error("❌ Error fetching states:", error);
        throw error;
    }
};

export const updateState = async (items = {}) => {
    const params = {
        TableName: STATES_TABLE_NAME,
        Key: marshall({ id: "states" }),
        UpdateExpression: "SET states = :states",
        ExpressionAttributeValues: marshall({
            ":states": items.states || items,
        }),
        ReturnValues: "ALL_NEW",
    };

    try {
        const { Attributes } = await dynamoDB.send(new UpdateItemCommand(params));
        return unmarshall(Attributes);
    } catch (error) {
        console.error("❌ Error updating state:", error);
        throw error;
    }
};
